import type { ComponentProps } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import dynamic from 'next/dynamic';
import useTheme from 'src/hooks/useTheme';
import { ColorKey } from 'src/styles/color';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { LoginBrandName } from 'src/types/Login';
import { pushEvent } from 'src/utils/event-gateway';
const RootStyle = styled.div<{
  backgroundColor: string;
  color: string;
  border?: string;
}>`
  background-color: ${props => props.backgroundColor};
  color: ${props => props.color};
  width: 280px;
  height: 52px;
  border-radius: 28px;
  justify-content: center;
  align-items: center;
  display: flex;
  font-weight: 700;
  padding: 14px 18px;
  margin-bottom: 8px;
  cursor: pointer;
  ${({
  border
}) => !!border && `border: 1px solid ${border};`}
  ${({
  theme
}) => theme.screenSize.mobile} {
    width: 100%;
  }
`;
const IcGoogle = styled.img`
  margin-top: -2px;
`;
const Label = styled.span`
  flex: 1;
  text-align: center;
  font-size: 15px;
`;
const IconCss = ({
  theme
}: {
  theme: Theme;
}) => css`
  margin-top: -2px;
  width: 21px;
  height: 20px;
  fill: ${theme.color.contentDeep};
`;
const IcPhone = styled(dynamic(() => import('src/assets/images/icPhone.svg')))`
  ${IconCss}
`;
const IcFacebook = styled(dynamic(() => import('src/assets/images/imgBrandFB.svg')))`
  ${IconCss}
`;
const IcApple = styled(dynamic(() => import('src/assets/images/imgBrandApple.svg')))`
  ${IconCss}
  fill: #0D0D0D;
`;
const Info: { [key in LoginBrandName]: {
  icon: React.ReactNode;
  fontColorKey: ColorKey;
  label: string; // crowdin key 대체 예정
  /**
   * 명시하지 않으면 border 미적용
   */
  borderColorKey?: ColorKey;

  /**
   * 명시하지 않으면 배경 미적용 (=transparent)
   */
  backgroundColorKey?: ColorKey;
} } = {
  [LoginBrandName.PHONE]: {
    icon: <IcPhone />,
    fontColorKey: 'content12',
    label: 'Continue with Phone',
    borderColorKey: 'surface9'
  },
  [LoginBrandName.FACEBOOK]: {
    icon: <IcFacebook />,
    backgroundColorKey: 'facebook1',
    fontColorKey: 'content12',
    label: 'Continue with Facebook'
  },
  [LoginBrandName.GOOGLE]: {
    icon: <IcGoogle src='/images/icons/imgBrandGoogle.svg' alt='google' />,
    backgroundColorKey: 'surface12',
    fontColorKey: 'content12Fixed',
    label: 'Continue with Google'
  },
  [LoginBrandName.APPLE]: {
    icon: <IcApple />,
    backgroundColorKey: 'surface12',
    fontColorKey: 'content12Fixed',
    label: 'Continue with Apple'
  }
};
interface Props extends Pick<ComponentProps<'div'>, 'onClick' | 'className'> {
  brand: LoginBrandName;
}
const LoginButton = ({
  brand,
  onClick,
  ...props
}: Props) => {
  const info = Info[brand];
  const theme = useTheme();
  return <RootStyle {...props} onClick={event => {
    event.preventDefault();
    onClick?.(event);
    pushEvent({
      eventType: EVENT_TYPE.SIGN_UP,
      eventName: EVENT_NAME.SCREEN_OPEN__LOGIN_METHOD,
      eventParams: {
        Method: brand.toLowerCase()
      }
    });
  }} color={theme.color[info.fontColorKey]} backgroundColor={info.backgroundColorKey ? theme.color[info.backgroundColorKey] : 'transparent'} border={info.borderColorKey ? theme.color[info.borderColorKey] : undefined} role='button' tabIndex={0} data-sentry-element="RootStyle" data-sentry-component="LoginButton" data-sentry-source-file="Button.tsx">
      {info.icon}
      <Label data-sentry-element="Label" data-sentry-source-file="Button.tsx">{info.label}</Label>
    </RootStyle>;
};
export default LoginButton;